/**
 * Property map components
 * PURPOSE: Displays a saved property position and lets owners/admins choose it on a free Leaflet map.
 *
 * COST:
 * - Leaflet is open-source and does not require an API key.
 * - The map below uses OpenStreetMap tiles and keeps the required attribution visible.
 *
 * EDITING GUIDE:
 * - Change TUNISIA_CENTER to alter the default map position before a property point is selected.
 * - Change DEFAULT_PICKER_ZOOM or DEFAULT_DETAILS_ZOOM to alter the initial zoom level.
 * - Keep the OpenStreetMap attribution visible to comply with the tile licence.
 */

import React, { useEffect, useMemo, useRef } from 'react';
import './PropertyMap.css';

// EDIT HERE: default center used when a new listing does not have coordinates yet.
const L = typeof window !== 'undefined' ? window.L : null;

const TUNISIA_CENTER = [34.0, 9.0];
const DEFAULT_PICKER_ZOOM = 6;
const DEFAULT_DETAILS_ZOOM = 16;

// OpenStreetMap tile address. No API key is required.
const OSM_TILE_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
const OSM_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

/**
 * Convert a form value to a valid numeric latitude or longitude.
 * Empty or invalid values return null so the map never receives NaN.
 */
function numberOrNull(value) {
  if (value === '' || value === undefined || value === null) return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

/**
 * Confirm that both coordinates are valid geographic values.
 */
export function hasValidPropertyCoordinates(latitude, longitude) {
  const lat = numberOrNull(latitude);
  const lng = numberOrNull(longitude);
  return lat !== null && lng !== null && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
}

/**
 * Build a marker without relying on Leaflet's image files.
 * This avoids broken marker icons after a React production build.
 */
function createPropertyPinIcon() {
  return L.divIcon({
    className: 'capital-map-pin-wrapper',
    html: '<span class="capital-map-pin" aria-hidden="true"></span>',
    iconSize: [30, 42],
    iconAnchor: [15, 42],
    popupAnchor: [0, -40],
  });
}

/**
 * Add the standard OpenStreetMap tile layer to a Leaflet map.
 */
function addOpenStreetMapLayer(map) {
  return L.tileLayer(OSM_TILE_URL, {
    attribution: OSM_ATTRIBUTION,
    maxZoom: 19,
  }).addTo(map);
}

/**
 * Read-only property map shown on the public details page.
 * It renders only when the listing has a valid latitude and longitude.
 */
export function PropertyLocationMap({ latitude, longitude, title, location }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const valid = hasValidPropertyCoordinates(latitude, longitude);
  const coordinates = useMemo(() => (
    valid ? [Number(latitude), Number(longitude)] : null
  ), [latitude, longitude, valid]);

  useEffect(() => {
    if (!L || !containerRef.current || !coordinates) return undefined;

    // Create the map once for this component instance.
    const map = L.map(containerRef.current, {
      center: coordinates,
      zoom: DEFAULT_DETAILS_ZOOM,
      scrollWheelZoom: false,
    });
    mapRef.current = map;
    addOpenStreetMapLayer(map);

    // The popup repeats the listing title/location so visitors know which point they opened.
    const marker = L.marker(coordinates, {
      icon: createPropertyPinIcon(),
      title: title || location || 'Property location',
      alt: title || location || 'Property location',
    }).addTo(map);
    markerRef.current = marker;

    const popupParts = [title, location].filter(Boolean).map((text) => String(text).replace(/[<>&"']/g, ''));
    if (popupParts.length) marker.bindPopup(popupParts.join('<br>'));

    // Leaflet sometimes needs a size refresh after the surrounding card animation finishes.
    const resizeTimer = window.setTimeout(() => map.invalidateSize(), 150);

    return () => {
      window.clearTimeout(resizeTimer);
      markerRef.current = null;
      mapRef.current = null;
      map.remove();
    };
  }, [coordinates, location, title]);

  if (!valid) return null;

  return <div ref={containerRef} className="property-location-map" aria-label={title || location || 'Property map'} />;
}

/**
 * Interactive map used in create/edit forms.
 * Clicking the map updates the controlled latitude and longitude values in the parent form.
 */
export function PropertyMapPicker({ latitude, longitude, onChange, required = false }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const valid = hasValidPropertyCoordinates(latitude, longitude);
  const selectedCoordinates = useMemo(() => (
    valid ? [Number(latitude), Number(longitude)] : null
  ), [latitude, longitude, valid]);

  // Keep the latest parent callback available to Leaflet event handlers without rebuilding the map.
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  useEffect(() => {
    if (!L || !containerRef.current) return undefined;

    const initialCenter = selectedCoordinates || TUNISIA_CENTER;
    const map = L.map(containerRef.current, {
      center: initialCenter,
      zoom: selectedCoordinates ? DEFAULT_DETAILS_ZOOM : DEFAULT_PICKER_ZOOM,
      scrollWheelZoom: false,
    });
    mapRef.current = map;
    addOpenStreetMapLayer(map);

    // Clicking anywhere on the map selects the exact property position.
    const handleMapClick = (event) => {
      const nextLatitude = Number(event.latlng.lat.toFixed(6));
      const nextLongitude = Number(event.latlng.lng.toFixed(6));
      onChangeRef.current?.({ latitude: nextLatitude, longitude: nextLongitude });
    };
    map.on('click', handleMapClick);

    const resizeTimer = window.setTimeout(() => map.invalidateSize(), 150);
    return () => {
      window.clearTimeout(resizeTimer);
      map.off('click', handleMapClick);
      markerRef.current = null;
      mapRef.current = null;
      map.remove();
    };
    // onChange is intentionally omitted because inline parent callbacks would recreate the whole map.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (selectedCoordinates) {
      if (!markerRef.current) {
        markerRef.current = L.marker(selectedCoordinates, {
          icon: createPropertyPinIcon(),
          draggable: true,
          title: 'Position du bien',
          alt: 'Position du bien',
        }).addTo(map);

        // Dragging the marker offers a more precise adjustment after the first click.
        markerRef.current.on('dragend', () => {
          const position = markerRef.current.getLatLng();
          onChangeRef.current?.({
            latitude: Number(position.lat.toFixed(6)),
            longitude: Number(position.lng.toFixed(6)),
          });
        });
      } else {
        markerRef.current.setLatLng(selectedCoordinates);
      }
      map.setView(selectedCoordinates, Math.max(map.getZoom(), 14), { animate: true });
    } else if (markerRef.current) {
      markerRef.current.remove();
      markerRef.current = null;
    }
  }, [selectedCoordinates]);

  /** Update only one coordinate while preserving the other controlled form value. */
  const updateCoordinate = (key, rawValue) => {
    onChangeRef.current?.({
      latitude: key === 'latitude' ? rawValue : latitude,
      longitude: key === 'longitude' ? rawValue : longitude,
    });
  };

  /** Use browser geolocation only after the user explicitly presses the button. */
  const useCurrentPosition = () => {
    if (!navigator.geolocation) {
      window.alert("La géolocalisation n'est pas disponible dans ce navigateur.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => onChangeRef.current?.({
        latitude: Number(position.coords.latitude.toFixed(6)),
        longitude: Number(position.coords.longitude.toFixed(6)),
      }),
      () => window.alert("Impossible d'obtenir votre position. Vous pouvez cliquer directement sur la carte."),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  return (
    <section className="property-map-picker form-field-full full">
      <div className="property-map-picker-heading">
        <div>
          <h3>Localisation sur la carte {required ? '*' : ''}</h3>
          <p>Cliquez sur la carte ou déplacez le repère pour enregistrer la position affichée dans la fiche du bien.</p>
        </div>
        <button type="button" className="map-location-button" onClick={useCurrentPosition}>Utiliser ma position</button>
      </div>

      <div ref={containerRef} className="property-map-picker-canvas" aria-label="Choisir la position du bien" />

      <div className="property-coordinate-inputs">
        <label>
          <span>Latitude</span>
          <input
            type="number"
            min="-90"
            max="90"
            step="0.000001"
            value={latitude ?? ''}
            onChange={(event) => updateCoordinate('latitude', event.target.value)}
            required={required}
            placeholder="36.806500"
          />
        </label>
        <label>
          <span>Longitude</span>
          <input
            type="number"
            min="-180"
            max="180"
            step="0.000001"
            value={longitude ?? ''}
            onChange={(event) => updateCoordinate('longitude', event.target.value)}
            required={required}
            placeholder="10.181500"
          />
        </label>
        <button type="button" className="map-clear-button" onClick={() => onChangeRef.current?.({ latitude: '', longitude: '' })}>Effacer le point</button>
      </div>
      <small>Conseil : pour protéger la vie privée du propriétaire, placez le repère près du bien plutôt que sur une porte précise si nécessaire.</small>
    </section>
  );
}
